import { useEffect, useRef, useState } from 'react';
import { ChatMessage, QuickQuestion } from './lib';
import {
  ChatSession,
  createChatSession,
} from '../../../../shared/api/chat-ai/chat-stream';
import {
  createTypewriter,
  Typewriter,
} from '../../../../shared/api/chat-ai/typewriter';
import { PageContext, useAi, WsDoneEvent } from '../../../../shared';
import { useDebouncedEffect } from '@coverright/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseChatOptions {
  initialMessage?: string;
  quickQuestions?: QuickQuestion[];
  pageContext?: PageContext;
}

export interface UseChatStreamReturn {
  messages: ChatMessage[];
  /** Partial text currently being typed — show as live AI bubble */
  streamingText: string;
  /** Human-readable label of the current thinking step */
  thinkingLabel: string;
  /** Accumulated list of thinking step labels received so far */
  thinkingSteps: string[];
  /** True while waiting for or receiving a response */
  isTyping: boolean;
  hasReplies: boolean;
  quickQuestions: QuickQuestion[];
  input: string;
  setInput: (v: string) => void;
  sendMessage: (text: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleQuickQuestion: (label: string) => void;
  handleClarification: (option: string) => void;
  /** Remove last AI response and re-request it */
  retryLast: () => void;
  /** Force-flush the typewriter (skip animation, show full text instantly) */
  flushTypewriter: () => void;
  lastResponseRef: React.RefObject<HTMLDivElement>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useChat({
  initialMessage = "Hi! I'm your health insurance assistant. How can I help you today?",
  quickQuestions = [],
  pageContext,
}: UseChatOptions = {}): UseChatStreamReturn {
  const {initChatSession} = useAi();
  const [messages, setMessages] = useState<ChatMessage[]>([
    { text: initialMessage, isUser: false, timestamp: new Date() },
  ]);
  const [streamingText, setStreamingText] = useState('');
  const [thinkingLabel, setThinkingLabel] = useState('');
  const [thinkingSteps, setThinkingSteps] = useState<string[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [input, setInput] = useState('');

  const sessionRef        = useRef<ChatSession | null>(null);
  const typewriterRef     = useRef<Typewriter | null>(null);
  const cancelRef         = useRef<(() => void) | null>(null);
  const doneEventRef      = useRef<WsDoneEvent | null>(null);
  const lastUserMsgRef    = useRef<string>('');
  /** How many times we have auto-retried the current question after a session death */
  const sessionRetryRef   = useRef<number>(0);

  const lastResponseRef   = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ── Session lifecycle ──────────────────────────────────────────────────────

  useDebouncedEffect(() => {
    if (pageContext) {
      sessionRef.current?.close();
      initChatSession(pageContext)?.then(() => {
        sessionRef.current = createChatSession();
      });
    }

    return () => {
      sessionRef.current?.close();
      sessionRef.current = null;
    };
  }, 400, [pageContext]);

  useEffect(() => {
    return () => {
      sessionRef.current?.close();
    };
  }, []);

  // ── Auto-scroll ────────────────────────────────────────────────────────────

  // Scroll on committed messages (user bubble → smooth to bottom;
  // AI bubble → smooth so the top of the response is visible)
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    if (last.isUser) {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
    } else {
      const el = lastResponseRef.current;
      if (el) container.scrollTo({ top: el.offsetTop - 100, behavior: 'smooth' });
    }
  }, [messages]);

  // During streaming: pin to bottom on every typewriter tick.
  // Use instant (no animation) — the text itself is the animation.
  useEffect(() => {
    if (!streamingText) return;
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [streamingText]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, [thinkingSteps]);

  // ── Send ───────────────────────────────────────────────────────────────────

  /** Internal: start streaming an answer without touching the messages list */
  const askQuestion = (text: string) => {
    if (!text.trim() || !sessionRef.current) return;

    setIsTyping(true);
    setStreamingText('');
    setThinkingLabel('');
    setThinkingSteps([]);
    doneEventRef.current = null;

    cancelRef.current?.();
    typewriterRef.current?.cancel();

    // New typewriter for this answer
    typewriterRef.current = createTypewriter({
      charsPerTick: 3,
      intervalMs: 16,
      onUpdate: (displayed) => setStreamingText(displayed),
      onComplete: () => {
        const ev = doneEventRef.current;
        if (!ev) return;

        // Commit the final AI message
        setMessages(prev => [...prev, {
          text: ev.answer,
          isUser: false,
          timestamp: new Date(),
          followups: ev.followups?.length ? ev.followups : undefined,
          clarificationOptions: ev.needs_clarification
            ? ev.clarification_options
            : undefined,
        }]);

        setStreamingText('');
        setThinkingLabel('');
        setIsTyping(false);
      },
    });

    // Ask via WebSocket
    cancelRef.current = sessionRef.current.ask(
      {
        question: text,
        bid_id: pageContext?.bid_id ?? null,
      },
      {
        onThinking: (event) => {
          setThinkingLabel(event.label);
          setThinkingSteps(prev => [...prev, event.label]);
        },

        onToken: (chunk) => {
          // First token hides the thinking label
          setThinkingLabel('');
          typewriterRef.current?.push(chunk);
        },

        onDone: (event) => {
          doneEventRef.current = event;
          typewriterRef.current?.seal();
        },

        onError: (event) => {
          typewriterRef.current?.cancel();
          setStreamingText('');
          setThinkingLabel('');

          // ── Timeout recovery ────────────────────────────────────────────────
          // chat-stream.ts exhausted MAX_RECONNECT_RETRIES without a response.
          // If no tokens were received yet (streamingText is empty) and we
          // haven't already retried, silently rebuild the session and re-ask.
          const question = lastUserMsgRef.current;
          if (
            event.code === 'timeout' &&
            question &&
            pageContext &&
            sessionRetryRef.current < 2
          ) {
            sessionRetryRef.current += 1;
            sessionRef.current?.close();
            initChatSession(pageContext).then(() => {
              sessionRef.current = createChatSession();
              // ask() queues the request; drainQueue fires after auth_ok
              askQuestion(question);
            });
            return; // keep isTyping=true — the question is still in flight
          }
          // ────────────────────────────────────────────────────────────────────

          setIsTyping(false);
          sessionRetryRef.current = 0;
          setMessages(prev => [...prev, {
            text: `<em>Something went wrong: ${event.message}</em>`,
            isUser: false,
            timestamp: new Date(),
          }]);
        },
      }
    );
  };

  // ── Public send + retry ────────────────────────────────────────────────────

  const sendMessage = (text: string) => {
    if (!text.trim() || isTyping) return;
    lastUserMsgRef.current = text;
    sessionRetryRef.current = 0; // reset retry budget for each new user question
    setMessages(prev => [...prev, { text, isUser: true, timestamp: new Date() }]);
    askQuestion(text);
  };

  const retryLast = () => {
    if (!lastUserMsgRef.current || isTyping) return;
    // Drop the last AI bubble and re-ask
    setMessages(prev => {
      const idx = [...prev].reverse().findIndex(m => !m.isUser);
      if (idx === -1) return prev;
      return prev.filter((_, i) => i !== prev.length - 1 - idx);
    });
    askQuestion(lastUserMsgRef.current);
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
    setInput('');
  };

  const handleQuickQuestion = (label: string) => sendMessage(label);
  const handleClarification = (option: string) => sendMessage(option);
  const flushTypewriter = () => typewriterRef.current?.flush();

  const hasReplies = messages.length > 1 || isTyping;

  return {
    messages,
    streamingText,
    thinkingLabel,
    thinkingSteps,
    isTyping,
    hasReplies,
    quickQuestions,
    input,
    setInput,
    sendMessage,
    handleSubmit,
    handleQuickQuestion,
    handleClarification,
    retryLast,
    flushTypewriter,
    lastResponseRef,
    scrollContainerRef,
  };
}
